// =============================================================================
// UserManagement.jsx
//
// Defensive version with full error protection and logging.
// Every API call and data access is wrapped in try/catch.
// =============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Paper, Stack, Button, IconButton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, CircularProgress, Alert, Dialog, DialogTitle, DialogContent,
    DialogActions, FormControl, InputLabel, Select, MenuItem,
    TextField, Tooltip, InputAdornment,
} from '@mui/material';
import {
    PersonAdd as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Search as SearchIcon,
    Shield as ManagerIcon,
    Person as RegularIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';

import {
    apiGetAllRoles,
    apiUpsertRole,
    apiDeleteRole,
    apiGetAllPortalUsers,
} from '../requests/weeklyUpdateRequests';

const LOG = (...args) => console.log('%c[UserMgmt]', 'color:#9c27b0;font-weight:bold', ...args);

const UserManagement = () => {
    LOG('RENDER');

    const [users, setUsers]               = useState([]);
    const [portalUsers, setPortalUsers]   = useState([]);
    const [loading, setLoading]           = useState(true);
    const [error, setError]               = useState(null);
    const [search, setSearch]             = useState('');

    const [dialogOpen, setDialogOpen]     = useState(false);
    const [dialogMode, setDialogMode]     = useState('add');
    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedRole, setSelectedRole] = useState('Regular');
    const [dialogLoading, setDialogLoading] = useState(false);

    const [deleteDialog, setDeleteDialog] = useState({ open: false, userId: '', userName: '' });

    // ── Load data ────────────────────────────────────────────────────────────
    const loadData = async () => {
        LOG('loadData START');
        setLoading(true);
        setError(null);

        // Load roles
        try {
            LOG('Calling apiGetAllRoles...');
            const rolesRes = await apiGetAllRoles();
            LOG('apiGetAllRoles OK, status:', rolesRes?.status, 'data length:', rolesRes?.data?.length);
            setUsers(Array.isArray(rolesRes?.data) ? rolesRes.data : []);
        } catch (err) {
            LOG('apiGetAllRoles FAILED:', err?.response?.status, err?.message);
            setError('Failed to load user roles.');
            setLoading(false);
            return; // Stop here, don't try portal users
        }

        // Load portal users (non-critical)
        try {
            LOG('Calling apiGetAllPortalUsers...');
            const portalRes = await apiGetAllPortalUsers();
            LOG('apiGetAllPortalUsers OK, status:', portalRes?.status, 'data length:', portalRes?.data?.length);
            setPortalUsers(Array.isArray(portalRes?.data) ? portalRes.data : []);
        } catch (err) {
            LOG('apiGetAllPortalUsers FAILED (non-critical):', err?.response?.status, err?.message);
            // Don't block the UI for this
        }

        setLoading(false);
        LOG('loadData COMPLETE');
    };

    useEffect(() => {
        LOG('MOUNT - calling loadData');
        loadData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Filter ───────────────────────────────────────────────────────────────
    const filteredUsers = useMemo(() => {
        try {
            if (!search.trim()) return users;
            const q = search.toLowerCase();
            return users.filter(u =>
                (u.userName || '').toLowerCase().includes(q) ||
                (u.email || '').toLowerCase().includes(q)
            );
        } catch {
            return users;
        }
    }, [users, search]);

    const availablePortalUsers = useMemo(() => {
        try {
            const existingIds = new Set(users.map(u => u.userId));
            return portalUsers.filter(p => !existingIds.has(p.id));
        } catch {
            return [];
        }
    }, [portalUsers, users]);

    // ── Handlers ─────────────────────────────────────────────────────────────
    const openAddDialog = () => {
        setDialogMode('add');
        setSelectedUserId('');
        setSelectedRole('Regular');
        setDialogOpen(true);
    };

    const openEditDialog = (user) => {
        setDialogMode('edit');
        setSelectedUserId(user.userId);
        setSelectedRole(user.role || 'Regular');
        setDialogOpen(true);
    };

    const handleDialogSave = async () => {
        if (!selectedUserId) return;
        setDialogLoading(true);
        try {
            await apiUpsertRole(selectedUserId, selectedRole);
            setDialogOpen(false);
            await loadData();
        } catch (err) {
            LOG('UpsertRole FAILED:', err?.message);
            setError('Failed to save user role.');
        } finally {
            setDialogLoading(false);
        }
    };

    const handleDelete = async () => {
        const { userId } = deleteDialog;
        setDeleteDialog({ open: false, userId: '', userName: '' });
        try {
            await apiDeleteRole(userId);
            await loadData();
        } catch (err) {
            LOG('DeleteRole FAILED:', err?.message);
            setError('Failed to remove user.');
        }
    };

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="subtitle1" fontWeight={700}>Module Users</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {users.length} user{users.length !== 1 ? 's' : ''} with access
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Tooltip title="Refresh">
                        <span>
                            <IconButton size="small" onClick={loadData} disabled={loading}>
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Button
                        variant="contained" size="small" startIcon={<AddIcon />}
                        onClick={openAddDialog}
                        sx={{ textTransform: 'none', borderRadius: 2 }}
                    >
                        Add User
                    </Button>
                </Stack>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <TextField
                fullWidth size="small" placeholder="Search users..."
                value={search} onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                        </InputAdornment>
                    ),
                }}
                sx={{ mb: 2 }}
            />

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress size={28} />
                </Box>
            ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'action.hover' }}>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>User</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Email</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Role</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem', width: 100 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredUsers.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                                        {search ? 'No users match your search.' : 'No users added yet.'}
                                    </TableCell>
                                </TableRow>
                            )}
                            {filteredUsers.map((user, idx) => (
                                <TableRow key={user.userId || idx} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600}>{user.userName || '—'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">{user.email || '—'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={user.role === 'Manager' ? <ManagerIcon sx={{ fontSize: 14 }} /> : <RegularIcon sx={{ fontSize: 14 }} />}
                                            label={user.role || 'Unknown'}
                                            size="small"
                                            color={user.role === 'Manager' ? 'primary' : 'default'}
                                            variant={user.role === 'Manager' ? 'filled' : 'outlined'}
                                            sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                            <Tooltip title="Change role">
                                                <IconButton size="small" onClick={() => openEditDialog(user)}>
                                                    <EditIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Remove access">
                                                <IconButton size="small" color="error"
                                                    onClick={() => setDeleteDialog({ open: true, userId: user.userId, userName: user.userName || '' })}>
                                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Add/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {dialogMode === 'add' ? 'Add User' : 'Change Role'}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2.5} sx={{ mt: 1 }}>
                        {dialogMode === 'add' && (
                            <FormControl fullWidth size="small">
                                <InputLabel>User</InputLabel>
                                <Select value={selectedUserId} label="User"
                                    onChange={(e) => setSelectedUserId(e.target.value)}>
                                    {availablePortalUsers.map((u) => (
                                        <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                                    ))}
                                    {availablePortalUsers.length === 0 && (
                                        <MenuItem disabled>All portal users already added</MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        )}
                        <FormControl fullWidth size="small">
                            <InputLabel>Role</InputLabel>
                            <Select value={selectedRole} label="Role"
                                onChange={(e) => setSelectedRole(e.target.value)}>
                                <MenuItem value="Manager">Manager</MenuItem>
                                <MenuItem value="Regular">Regular</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleDialogSave}
                        disabled={!selectedUserId || dialogLoading} sx={{ textTransform: 'none' }}>
                        {dialogLoading ? <CircularProgress size={18} /> : (dialogMode === 'add' ? 'Add' : 'Save')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, userId: '', userName: '' })}>
                <DialogTitle>Remove User Access</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Remove <strong>{deleteDialog.userName}</strong> from the Weekly Update module?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, userId: '', userName: '' })} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained" sx={{ textTransform: 'none' }}>Remove</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default UserManagement;